import { useCallback, useEffect, useMemo, useState } from "react";
import type { Key } from "react-aria-components";

import { trpc } from "@blis/trpc/react";

import { Link } from "@/components/Link";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogBody,
  DialogCloseButton,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { GridList, GridListItem } from "@/components/ui/grid-list";
import { ItemDetail } from "@/components/ui/list-box";
import { Modal } from "@/components/ui/modal";
import { Separator } from "@/components/ui/separator";
import { Text } from "@/components/ui/text";
import { mutationResponse } from "@/utils/trpc";
import type { PanelDetail } from "../../types";
import { useAdminGridOptions } from "../../utils/gridState";
import { AdminGrid } from "../AdminGrid";
import { TabLayout } from "../TabLayout";

export function PanelTestsTable({ panel }: { panel: PanelDetail }) {
  const testOptions = useAdminGridOptions(
    panel.tests,
    [
      {
        field: "code",
        width: 200,
        maxWidth: 200,
        cellRenderer: (params) => (
          <Link to="/admin/tests/$id/edit" params={{ id: params.id }}>
            {params.code}
          </Link>
        ),
        cellRendererParams: (params) => ({
          id: params.data.id,
          code: params.data.code,
        }),
      },
      {
        field: "name",
        cellRenderer: (params) => (
          <Link to="/admin/tests/$id/edit" params={{ id: params.id }}>
            {params.name}
          </Link>
        ),
        cellRendererParams: (params) => ({
          id: params.data.id,
          name: params.data.name,
        }),
      },
    ],
    { sideBar: undefined },
  );

  const panelTests = useMemo(() => {
    return panel.panels.flatMap((p) =>
      p.tests.map((t) => ({ ...t, panel: p.name })),
    );
  }, [panel.panels]);

  const panelOptions = useAdminGridOptions(
    panelTests,
    [
      {
        field: "name",
        cellRenderer: (params) => (
          <Link to="/admin/panels/$id/edit" params={{ id: params.id }}>
            {params.name}
          </Link>
        ),
        cellRendererParams: (params) => ({
          id: params.data.id,
          name: params.data.name,
        }),
      },
      {
        field: "panel",
        headerName: "Sub-panel",
      },
    ],
    { sideBar: undefined },
  );

  return (
    <TabLayout
      description="Test specs included in this panel"
      actions={<UpdateTestsDialog panel={panel} />}
    >
      <div className="flex h-96">
        <AdminGrid {...testOptions.options} />
      </div>

      {panel.panels.length > 0 && (
        <>
          <Separator className="mt-6" />
          <div>
            <Text elementType="p" className="pt-6">
              Tests included from sub-panels
            </Text>
            <Text muted size="2" className="pb-6" elementType="p">
              Edit these in the{" "}
              <Link
                // biome-ignore lint/suspicious/noExplicitAny: TODO: nothing else works in tanstack router
                to={"./?t=subpanels" as any}
                params={{}}
              >
                Sub-panels
              </Link>{" "}
              tab
            </Text>
          </div>
          <div className="flex h-96">
            <AdminGrid {...panelOptions.options} />
          </div>
        </>
      )}
    </TabLayout>
  );
}

function UpdateTestsDialog({ panel }: { panel: PanelDetail }) {
  const [open, setOpen] = useState(false);
  const [keys, setKeys] = useState<Key[]>(panel.tests.map((t) => t.id));
  const { data } = trpc.tests.all.useQuery();

  const updateTests = trpc.admin.panel.updateTests.useMutation();

  useEffect(() => {
    if (open) {
      setKeys(panel.tests.map((t) => t.id));
    }
  }, [panel.tests, open]);

  const onSelectionChange = useCallback(
    (keys: "all" | Set<Key>) => {
      if (keys === "all") {
        setKeys(data?.map((t) => t.id) || []);
      } else {
        setKeys(Array.from(keys));
      }
    },
    [data],
  );

  const onUpdate = useCallback(() => {
    updateTests.mutate(
      { id: panel.id, testIds: keys.map((k) => Number(k)) },
      mutationResponse({
        errorTitle: "Failed to update tests",
        successMessage: "Tests updated",
        onSuccess: () => setOpen(false),
      }),
    );
  }, [keys, panel.id, updateTests]);

  return (
    <DialogTrigger isOpen={open} onOpenChange={setOpen}>
      <Button variant="secondary" onPress={() => setOpen(true)}>
        Edit tests
      </Button>
      <Modal>
        <Dialog>
          <DialogHeader>
            <DialogTitle>Update tests</DialogTitle>
          </DialogHeader>
          <DialogBody>
            <GridList
              items={data || []}
              selectionMode="multiple"
              selectedKeys={keys}
              onSelectionChange={onSelectionChange}
              // isLoading={isLoading}
            >
              {(test) => (
                <GridListItem
                  key={test.id}
                  textValue={`${test.name} (${test.code})`}
                >
                  {test.name}
                  <ItemDetail>{test.code}</ItemDetail>
                </GridListItem>
              )}
            </GridList>
          </DialogBody>
          <DialogFooter>
            <Button isPending={updateTests.isPending} onPress={onUpdate}>
              Update
            </Button>
            <DialogCloseButton>Cancel</DialogCloseButton>
          </DialogFooter>
        </Dialog>
      </Modal>
    </DialogTrigger>
  );
}
